import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './index.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import App from './App';
import { HostRedirect } from './components/routing/HostRedirect';
import './i18n';
import i18n from './i18n';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { FontSizeProvider } from './context/FontSizeContext';
import { CartProvider } from './context/CartContext';
import { GeoAccessProvider } from './context/GeoAccessContext';

/** SPAs do not reset scroll on route changes; restore top-of-page after navigation. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function I18nHtmlLangSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const apply = () => {
      document.documentElement.lang = i18n.resolvedLanguage || i18n.language || 'en';
    };
    apply();
    i18n.on('languageChanged', apply);
    return () => {
      i18n.off('languageChanged', apply);
    };
  }, [i18n]);
  return null;
}

/** Keeps i18n in sync when `lewouhmart_lang` changes in another tab/window. */
function CrossTabLanguageSync() {
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'lewouhmart_lang' || e.newValue == null) return;
      const lng = e.newValue;
      if (lng === 'en' || lng === 'fr' || lng === 'es' || lng === 'de') {
        void i18n.changeLanguage(lng);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return null;
}

const rootElement = document.getElementById('root');

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <AuthProvider>
          <FontSizeProvider>
            <CartProvider>
              <CurrencyProvider>
                <GeoAccessProvider>
                  <ScrollToTop />
                  <I18nHtmlLangSync />
                  <CrossTabLanguageSync />
                  <HostRedirect />
                  <App />
                </GeoAccessProvider>
              </CurrencyProvider>
            </CartProvider>
          </FontSizeProvider>
        </AuthProvider>
      </BrowserRouter>
    </React.StrictMode>,
  );
}

reportWebVitals();

